import type DxfArrayScanner from 'dxf-parser/dist/DxfArrayScanner.js'
import type { IGroup } from 'dxf-parser/dist/DxfArrayScanner.js'
import type { IEntity, IPoint } from 'dxf-parser/dist/entities/geomtry.js'
import * as helpers from '@/plugins/cad/module/parser/parseHelpers'

/** 多重引线单条分支（箭头-肘部-引线末端）描述 */
export interface IDxfMLeaderBranch {
  /** 分支箭头所在点 */
  arrowPoint?: IPoint
  /** 分支折点列表，首个通常为肘点，末个通常为着陆终点 */
  vertices?: IPoint[]
  /** 引线肘部折点 */
  elbowPoint?: IPoint
  /** 引线末端连接到文字/基线的位置 */
  landingEndPoint?: IPoint
}

/** DXF MLEADER 实体解析结果 */
export interface IDxfMLeaderEntity extends IEntity {
  /** 文字锚点位置 */
  textPosition?: IPoint
  /** 文字字高，单位与 DXF 原始值一致 */
  textHeight?: number
  /** 文字内容，可能由多段 304 组拼接而成 */
  contents?: string
  /** 基线与文字的间距 */
  landingGap?: number
  /** 是否存在基线 */
  hasLanding?: boolean
  /** 引线分支集合 */
  branches: IDxfMLeaderBranch[]
}

/** DXF MLEADER 实体解析器，注册到 dxf-parser 后用于多重引线解析 */
export default class MLeaderEntityParser {
  /** dxf-parser 约定的实体名称键 */
  public ForEntityName = 'MLEADER' as any

  /**
   * 解析单个 MLEADER 实体。
   *
   * @param scanner - DXF 组码扫描器
   * @param curr - 当前已读取的组码项
   * @returns 解析后的 MLEADER 实体对象
   */
  public parseEntity(scanner: DxfArrayScanner, curr: IGroup) {
    const entity = {
      type: curr.value,
      branches: [],
    } as unknown as IDxfMLeaderEntity
    type ParseBranch = IDxfMLeaderBranch & {
      leaderLinePoints?: IPoint[]
      lastLeaderLinePoint?: IPoint
      doglegVector?: IPoint
      doglegLength?: number
    }
    let currentBranch: ParseBranch | null = null
    let inContextData = false
    let inLeader = false
    let inLeaderLine = false

    const ensureCurrentBranch = () => {
      currentBranch ??= {}
      if (!entity.branches.includes(currentBranch)) {
        entity.branches.push(currentBranch)
      }
      return currentBranch
    }

    const finishCurrentBranch = () => {
      if (!currentBranch || !currentBranch.leaderLinePoints?.length) {
        return
      }

      const leaderLinePoints = currentBranch.leaderLinePoints
      currentBranch.arrowPoint = leaderLinePoints[0]

      const vertices = leaderLinePoints.slice(1)
      if (currentBranch.lastLeaderLinePoint) {
        currentBranch.elbowPoint = currentBranch.lastLeaderLinePoint
        vertices.push(currentBranch.lastLeaderLinePoint)
      }

      const doglegLength = Math.abs(currentBranch.doglegLength ?? 0)
      if ((entity.hasLanding ?? true) && currentBranch.doglegVector && doglegLength > 1e-6) {
        const landingEndPoint = {
          x:
            (currentBranch.lastLeaderLinePoint?.x ?? currentBranch.arrowPoint.x) +
            currentBranch.doglegVector.x * doglegLength,
          y:
            (currentBranch.lastLeaderLinePoint?.y ?? currentBranch.arrowPoint.y) +
            currentBranch.doglegVector.y * doglegLength,
          z: currentBranch.lastLeaderLinePoint?.z ?? currentBranch.arrowPoint.z,
        }
        currentBranch.landingEndPoint = landingEndPoint
        vertices.push(landingEndPoint)
      }

      currentBranch.vertices = vertices
    }

    curr = scanner.next()
    while (!scanner.isEOF()) {
      if (curr.code === 0) {
        break
      }

      switch (curr.code) {
        case 300:
          if (curr.value === 'CONTEXT_DATA{') {
            inContextData = true
          }
          break
        case 301:
          if (curr.value === '}') {
            inContextData = false
          }
          break
        case 302:
          if (curr.value === 'LEADER{') {
            currentBranch = {}
            entity.branches.push(currentBranch)
            inLeader = true
            inLeaderLine = false
          }
          break
        case 303:
          if (curr.value === '}') {
            finishCurrentBranch()
            currentBranch = null
            inLeader = false
            inLeaderLine = false
          }
          break
        case 10:
          if (inLeaderLine) {
            ensureCurrentBranch().leaderLinePoints ??= []
            currentBranch!.leaderLinePoints!.push(helpers.parsePoint(scanner))
          } else if (inLeader) {
            ensureCurrentBranch().lastLeaderLinePoint = helpers.parsePoint(scanner)
          } else if (inContextData || entity.textPosition === undefined) {
            entity.textPosition = helpers.parsePoint(scanner)
          } else {
            helpers.parsePoint(scanner)
          }
          break
        case 40:
          if (inLeader) {
            ensureCurrentBranch().doglegLength = curr.value as number
          } else if (entity.textHeight === undefined) {
            entity.textHeight = curr.value as number
          }
          break
        case 41:
          if (inContextData && !inLeader) {
            entity.textHeight = curr.value as number
          }
          break
        case 90:
          break
        case 91:
          break
        case 11:
          if (inLeader) {
            ensureCurrentBranch().doglegVector = helpers.parsePoint(scanner)
          } else if (currentBranch && !inContextData) {
            currentBranch.arrowPoint = helpers.parsePoint(scanner)
            if (!entity.branches.includes(currentBranch)) {
              entity.branches.push(currentBranch)
            }
          } else {
            helpers.parsePoint(scanner)
          }
          break
        case 12:
          if (currentBranch && !inLeader && !inContextData) {
            currentBranch.elbowPoint = helpers.parsePoint(scanner)
            if (!entity.branches.includes(currentBranch)) {
              entity.branches.push(currentBranch)
            }
          } else if (inContextData) {
            const textLocation = helpers.parsePoint(scanner)
            entity.textPosition = {
              ...textLocation,
              y: textLocation.y - (entity.textHeight ?? 0) / 2,
            }
          } else {
            helpers.parsePoint(scanner)
          }
          break
        case 13:
          if (currentBranch && !inLeader && !inContextData) {
            currentBranch.landingEndPoint = helpers.parsePoint(scanner)
            if (!entity.branches.includes(currentBranch)) {
              entity.branches.push(currentBranch)
            }
          } else {
            helpers.parsePoint(scanner)
          }
          break
        case 145:
          entity.landingGap = curr.value as number
          break
        case 290:
          if (!inLeader) {
            entity.hasLanding = curr.value !== 0
          }
          break
        case 304:
          if (inLeader && curr.value === 'LEADER_LINE{') {
            ensureCurrentBranch().leaderLinePoints ??= []
            inLeaderLine = true
          } else if (!inLeader) {
            entity.contents = entity.contents
              ? `${entity.contents}${curr.value as string}`
              : (curr.value as string)
          }
          break
        case 305:
          if (curr.value === '}') {
            inLeaderLine = false
            finishCurrentBranch()
          }
          break
        default:
          helpers.checkCommonEntityProperties(entity, curr, scanner)
          break
      }

      curr = scanner.next()
    }

    entity.branches = entity.branches.filter(Boolean)
    return entity
  }
}

/** AutoCAD 写出的多重引线实体名通常为 MULTILEADER。 */
export class MultiLeaderEntityParser extends MLeaderEntityParser {
  public ForEntityName = 'MULTILEADER' as any
}
